/* eslint-disable @typescript-eslint/no-explicit-any */

import { uuidv4 } from '../utils/uuid';

const API_BASE_URL = '/api/v1';

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: any;
  params?: Record<string, string | readonly string[]>;
  idempotencyKey?: string;
}

class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private getCsrfToken(): string | null {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta?.getAttribute('content') ?? null;
  }

  private generateIdempotencyKey(): string {
    return uuidv4();
  }

  private buildUrl(
    path: string,
    params?: Record<string, string | readonly string[]>,
  ): string {
    const url = new URL(`${API_BASE_URL}${path}`, window.location.origin);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        /* Filtro de lista (`statusIn`) e repeticao da chave, nao valor unico:
           `set` sobrescrevia e so o ultimo item do array chegava ao servidor. */
        if (Array.isArray(value)) {
          url.searchParams.delete(key);
          for (const item of value) url.searchParams.append(key, item);
        } else {
          url.searchParams.set(key, value as string);
        }
      });
    }

    return url.toString();
  }

  private async request<T>(
    method: string,
    path: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { body, params, idempotencyKey, ...rest } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    // Add CSRF token
    const csrfToken = this.getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    // Add idempotency key for mutating requests
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      headers['Idempotency-Key'] = idempotencyKey ?? this.generateIdempotencyKey();
    }

    const response = await fetch(this.buildUrl(path, params), {
      method,
      headers,
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
      ...rest,
    });

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    const data = await response.json();

    if (!response.ok) {
      // Backend Nexora format: { error: { status, code, message, details } }
      const backendError = data as {
        error?: { status: number; code: string; message: string; details?: Record<string, unknown> };
      };

      // Also support RFC 9457 problem+json: { type, title, status, detail }
      const problem = data as {
        type?: string;
        title?: string;
        status: number;
        detail?: string;
        errors?: Record<string, unknown>;
      };

      const status = backendError.error?.status ?? problem.status ?? response.status;
      const code = backendError.error?.code ?? problem.type ?? 'unknown';
      const message = backendError.error?.message ?? problem.detail ?? problem.title ?? 'Erro desconhecido';
      const details = backendError.error?.details ?? problem.errors;

      throw new ApiError(status, code, message, details);
    }

    return data as T;
  }

  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, options);
  }

  async post<T>(path: string, body?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, { ...options, body });
  }

  async put<T>(path: string, body?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, { ...options, body });
  }

  async patch<T>(path: string, body?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, { ...options, body });
  }

  async delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }
}

export const apiClient = new ApiClient();
export { ApiError };
